import { ReviewChangeRequest } from "../../types";

export interface HistorySlot {
  flyerItemId: string;
  fieldKey: string;
  itemName?: string;
  crs: ReviewChangeRequest[];
  restoreEdges: { sourceId: string; revertId: string }[];
  latestActivityMs: number;
}

const toMs = (d: string | Date | undefined | null): number => {
  if (d === undefined || d === null) return 0;
  const t = d instanceof Date ? d.getTime() : new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const slotKey = (flyerItemId: string, fieldKey: string) =>
  `${flyerItemId}::${fieldKey}`;

export interface BuildHistorySlotsArgs {
  changeRequests: ReviewChangeRequest[];
  itemNameById?: Map<string, string | undefined>;
  filterFlyerItemId?: string;
}

export const buildHistorySlots = ({
  changeRequests,
  itemNameById,
  filterFlyerItemId,
}: BuildHistorySlotsArgs): HistorySlot[] => {
  const grouped = new Map<string, HistorySlot>();

  for (const cr of changeRequests) {
    if (!cr.flyerItemId || !cr.fieldKey) continue;
    if (filterFlyerItemId && cr.flyerItemId !== filterFlyerItemId) continue;
    const fk = String(cr.fieldKey);
    const key = slotKey(cr.flyerItemId, fk);
    let slot = grouped.get(key);
    if (!slot) {
      const newSlot: HistorySlot = {
        flyerItemId: cr.flyerItemId,
        fieldKey: fk,
        itemName: itemNameById?.get(cr.flyerItemId),
        crs: [],
        restoreEdges: [],
        latestActivityMs: 0,
      };
      grouped.set(key, newSlot);
      slot = newSlot;
    }
    slot.crs.push(cr);
    const ts = Math.max(
      toMs(cr.submittedAt),
      toMs(cr.createdAt),
      toMs(cr.resolvedAt),
      toMs(cr.metadata?.restoredAt),
    );
    if (ts > slot.latestActivityMs) slot.latestActivityMs = ts;
  }

  const slots: HistorySlot[] = [];
  grouped.forEach((slot) => {
    slot.crs.sort(
      (a, b) =>
        toMs(a.createdAt || a.submittedAt) - toMs(b.createdAt || b.submittedAt),
    );

    const idIndex = new Map<string, number>();
    slot.crs.forEach((cr, i) => idIndex.set(cr.id, i));

    const seenPairs = new Set<string>();
    for (const cr of slot.crs) {
      const restoreOf = cr.metadata?.restoreOf;
      if (restoreOf && idIndex.has(restoreOf)) {
        const pairKey = `${restoreOf}->${cr.id}`;
        if (!seenPairs.has(pairKey)) {
          seenPairs.add(pairKey);
          slot.restoreEdges.push({ sourceId: restoreOf, revertId: cr.id });
        }
      }
      const restoredByCrId = cr.metadata?.restoredByCrId;
      if (restoredByCrId && idIndex.has(restoredByCrId)) {
        const pairKey = `${cr.id}->${restoredByCrId}`;
        if (!seenPairs.has(pairKey)) {
          seenPairs.add(pairKey);
          slot.restoreEdges.push({
            sourceId: cr.id,
            revertId: restoredByCrId,
          });
        }
      }
    }

    slots.push(slot);
  });

  slots.sort((a, b) => b.latestActivityMs - a.latestActivityMs);
  return slots;
};
